import { useMemo, useState } from "react";

import { t } from "../../../i18n";
import { trpc } from "../../../lib/trpc";
import {
  Badge,
  Button,
  Card,
  CategoryIcon,
  DeletingOverlay,
  DropdownMenu,
  IconButton,
  Input,
  MenuItem,
  Switch,
  useToast,
} from "../../ui";
import { PatternChipRow } from "../components/PatternChipRow";
import { CategoryFormDialog } from "../dialogs/CategoryFormDialog";
import { DeleteCategoryDialog } from "../dialogs/DeleteCategoryDialog";
import type { CategoryItem } from "../utils";

interface CategoriesTabProps {
  householdId: string;
  categories: Array<CategoryItem>;
}

export function CategoriesTab({ householdId, categories }: CategoriesTabProps) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryItem | null>(null);
  const [deleting, setDeleting] = useState<CategoryItem | null>(null);
  const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(() => new Set());
  const [query, setQuery] = useState("");

  const remove = trpc.household.deleteCategory.useMutation({
    onMutate: ({ categoryId }) => {
      setPendingDeletions((prev) => {
        const next = new Set(prev);
        next.add(categoryId);
        return next;
      });
    },
    onSuccess: async (_, { categoryId }) => {
      await utils.household.get.invalidate({ groupId: householdId });
      setPendingDeletions((prev) => {
        if (!prev.has(categoryId)) return prev;
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
    },
    onError: (err, { categoryId }) => {
      setPendingDeletions((prev) => {
        if (!prev.has(categoryId)) return prev;
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
      show({
        tone: "danger",
        title: t.household.categories.deleteFailedToast,
        description: err.message,
        dismissLabel: t.common.close,
      });
    },
  });

  const confirmDelete = (category: CategoryItem) => {
    remove.mutate({ groupId: householdId, categoryId: category.id });
    setDeleting(null);
  };

  const update = trpc.household.updateCategory.useMutation({
    onMutate: async (input) => {
      await utils.household.get.cancel({ groupId: householdId });
      const prev = utils.household.get.getData({ groupId: householdId });
      utils.household.get.setData({ groupId: householdId }, (data) =>
        data
          ? {
              ...data,
              categories: data.categories.map((cat) =>
                cat.id === input.categoryId
                  ? { ...cat, defaultShared: input.category.defaultShared }
                  : cat,
              ),
            }
          : data,
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) utils.household.get.setData({ groupId: householdId }, ctx.prev);
      show({
        tone: "danger",
        title: t.household.categories.updateFailedToast,
        description: err.message,
        dismissLabel: t.common.close,
      });
    },
    onSuccess: (result, input) => {
      // Mock mode returns { ok }; real backend returns { id }. Only the latter
      // can carry a forked id we need to reconcile.
      if (!result || !("id" in result) || result.id === input.categoryId) return;
      const forkedId = result.id;
      utils.household.get.setData({ groupId: householdId }, (data) =>
        data
          ? {
              ...data,
              categories: data.categories.map((cat) =>
                cat.id === input.categoryId ? { ...cat, id: forkedId } : cat,
              ),
            }
          : data,
      );
    },
    onSettled: () => utils.household.get.invalidate({ groupId: householdId }),
  });

  const toggleShared = (c: CategoryItem) =>
    update.mutate({
      groupId: householdId,
      categoryId: c.id,
      category: {
        name: c.name,
        icon: c.icon,
        color: c.color,
        defaultShared: !c.defaultShared,
        matchPatterns: c.matchPatterns,
      },
    });

  // Sort alphabetically (case-insensitive, locale-aware for nb-NO) and
  // filter by the search term. Filters match the name or any of the
  // configured match-patterns — both are user-meaningful surfaces.
  const visibleCategories = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = needle
      ? categories.filter((c) => {
          if (c.name.toLowerCase().includes(needle)) return true;
          return c.matchPatterns.some((p) => p.toLowerCase().includes(needle));
        })
      : categories;
    return [...list].sort((a, b) => a.name.localeCompare(b.name, "nb"));
  }, [categories, query]);

  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-medium text-ink-700 text-sm">
            {t.household.categories.countHeading(categories.length)}
          </h2>
          <p className="mt-0.5 text-ink-500 text-xs">{t.household.categories.description}</p>
        </div>
        <Button
          variant="primary"
          iconLeft="plus"
          onClick={() => setCreateOpen(true)}
          className="shrink-0"
        >
          {t.household.categories.newCategory}
        </Button>
      </div>

      <div className="mb-3">
        <Input
          type="search"
          iconLeft="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.household.categories.searchPlaceholder}
        />
      </div>

      {visibleCategories.length === 0 && (
        <p className="mb-4 text-ink-500 text-sm">{t.household.categories.searchEmpty(query)}</p>
      )}

      <Card padding="none" className="overflow-hidden">
        <ul className="divide-y divide-ink-200 dark:divide-ink-300">
          {visibleCategories.map((c) => (
            <li
              key={c.id}
              className="relative p-3 even:bg-ink-50/40 sm:p-4 dark:even:bg-ink-100/20"
            >
              {pendingDeletions.has(c.id) && (
                <DeletingOverlay label={t.household.categories.deletingOverlay} />
              )}
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white"
                    style={{ backgroundColor: c.color }}
                  >
                    <CategoryIcon name={c.icon ?? "tag"} size="sm" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink-900 text-sm">{c.name}</span>
                      {c.isDefault && (
                        <Badge tone="neutral" iconLeft="sparkles">
                          {t.household.categories.defaultBadge}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <DropdownMenu trigger={<IconButton icon="more-vertical" label={t.common.more} />}>
                    <MenuItem icon="edit" onClick={() => setEditing(c)}>
                      {t.common.edit}
                    </MenuItem>
                    <MenuItem icon="trash" destructive onClick={() => setDeleting(c)}>
                      {t.common.delete}
                    </MenuItem>
                  </DropdownMenu>
                </div>
              </div>
              <div className="mt-2">
                <PatternChipRow
                  householdId={householdId}
                  category={c}
                  trailing={
                    <Switch
                      checked={c.defaultShared}
                      onCheckedChange={() => toggleShared(c)}
                      label={t.household.categories.sharedLabel}
                      className="flex-row-reverse [&_span]:text-xs"
                    />
                  }
                />
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <CategoryFormDialog
        mode="create"
        householdId={householdId}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <CategoryFormDialog
        mode="edit"
        householdId={householdId}
        category={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
      />
      <DeleteCategoryDialog
        category={deleting}
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
